/**
 * Shared page-slot rendering logic.
 * Used by both PagePreview (admin editor) and FrontPage (reader home).
 */
import { useMemo, useState } from 'react'
import { Lock } from 'lucide-react'
import { useAdminArticles, useArticlesByIds } from '../../api/hooks'
import { useWireAuth } from '../../auth/useWireAuth'
import { isArticleLocked } from '../../lib/articleAccess'
import { ArticleAccessDialog } from '../common/ArticleAccessDialog'
import type { CanvasSlot } from '../editor-shared/canvasTypes'
import type { BlockContent, WireArticle } from '../../api/types'
import { StoryRow, RankedList, UnrankedList } from '../page-blocks/implementations'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import { PageSlotInlineMetricsCard, type PageArticleInlineMetrics } from './PageInlineMetricsCard'
import {
  EDITORIAL_GRID_PRESET_CONFIG,
  type EditorialGridLayoutPreset,
} from '../page-blocks/definitions/editorialGrid'
import { COL_KEYS } from '../editor-shared/canvasTypes'

// ─── Date formatting ──────────────────────────────────────────────────────────

function formatArticleDate(iso: string | undefined): string | undefined {
  if (!iso) return undefined
  try {
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return undefined
  }
}

// ─── Recalled article helpers ─────────────────────────────────────────────────

const RECALL_TITLE = 'This Article Has Been Recalled'
function articleDisplayTitle(a: WireArticle) { return a.workflow?.stage === 'recalled' ? RECALL_TITLE : a.title }
function articleThumbnailUrl(a: WireArticle) { return a.workflow?.stage === 'recalled' ? undefined : a.thumbnail?.presignedUrl }
function articleDescription(a: WireArticle) { return a.workflow?.stage === 'recalled' ? undefined : a.description }

// ─── Pool capacity ────────────────────────────────────────────────────────────

/**
 * Returns the maximum number of articles a slot can display.
 * Used by pool distribution to know how many articles to consume per slot.
 */
export function poolCapacity(slot: CanvasSlot): number {
  switch (slot.type) {
    case 'hero-overlay': return 3
    case 'inline-feature': return 1
    case 'feature-with-rail': return 6
    case 'story-row': {
      const count = Number(slot.config.columnCount ?? 3)
      return ([2, 3, 4, 5].includes(count) ? count : 3)
    }
    case 'ranked-list': return 10
    case 'unranked-list': return 12
    case 'livewire-callout': return 1
    default: return 0
  }
}

// ─── Per-slot article resolution ──────────────────────────────────────────────

/** Pure slot resolution — no data fetching. Pass in an articleById lookup. */
function resolveSlotArticles(
  slots: CanvasSlot[],
  contentMap: Record<string, BlockContent>,
  articleById: Record<string, WireArticle>,
  poolArticles?: WireArticle[],
): Record<string, WireArticle[]> {
  const result: Record<string, WireArticle[]> = {}

  /** Backend bug workaround: articles returned with empty title/description are not renderable. */
  function hasContent(a: WireArticle): boolean {
    return (a.title ?? '').trim() !== ''
  }

  function resolveIds(ids: string[]): WireArticle[] {
    return ids.flatMap((id) => {
      const found = articleById[id]
      if (!found || !hasContent(found)) return []
      return [found]
    })
  }

  for (const slot of slots) {
    const content = contentMap[slot.id]

    if (content?.source === 'static') {
      result[slot.id] = resolveIds(content.articleIds?.filter(Boolean) ?? [])
    } else if (content != null && content.source !== 'pool') {
      // Explicit non-pool, non-static content — assign empty
      result[slot.id] = []
    }
    // else: null/undefined content means "inherit from pool" — leave unset for pool distribution

    if (slot.type === 'editorial-grid') {
      for (const colKey of COL_KEYS) {
        for (const colBlock of slot.columnBlocks?.[colKey] ?? []) {
          const colContentKey = `${slot.id}:${colKey}:${colBlock.id}`
          const colContent = contentMap[colContentKey]
          if (colContent?.source === 'static') {
            result[colContentKey] = resolveIds(colContent.articleIds?.filter(Boolean) ?? [])
          } else if (colContent != null && colContent.source !== 'pool') {
            result[colContentKey] = []
          }
          // else: null/undefined means "inherit from pool" — leave for pool distribution
        }
      }
    }
  }

  if (poolArticles && poolArticles.length > 0) {
    const filteredPool = poolArticles.filter(hasContent)
    poolArticles = filteredPool
    const sortedSlots = [...slots].sort((a, b) => a.order - b.order)
    let poolCursor = 0
    for (const slot of sortedSlots) {
      const content = contentMap[slot.id]
      const isPoolSourced = !content || content.source === 'pool'

      if (!isPoolSourced) continue

      if (slot.type === 'editorial-grid') {
        // editorial-grid is a layout container — distribute pool articles to its column blocks
        for (const colKey of COL_KEYS) {
          for (const colBlock of slot.columnBlocks?.[colKey] ?? []) {
            const colContentKey = `${slot.id}:${colKey}:${colBlock.id}`
            const colContent = contentMap[colContentKey]
            if (!colContent || colContent.source === 'pool') {
              const colSlot = { id: colBlock.id, type: colBlock.type, order: 0, config: {} } as CanvasSlot
              const cap = poolCapacity(colSlot)
              result[colContentKey] = poolArticles.slice(poolCursor, poolCursor + cap)
              poolCursor += cap
            }
          }
        }
      } else {
        const cap = poolCapacity(slot)
        result[slot.id] = poolArticles.slice(poolCursor, poolCursor + cap)
        poolCursor += cap
      }
    }
  }

  return result
}

/**
 * Admin version — fetches all drafts and resolves by ID client-side.
 * Used by the page editor preview where unpublished articles must be visible.
 */
export function useSlotArticles(
  slots: CanvasSlot[],
  contentMap: Record<string, BlockContent>,
  poolArticles?: WireArticle[],
): Record<string, WireArticle[]> {
  const { data: allDrafts = [] } = useAdminArticles()
  const articleById: Record<string, WireArticle> = Object.fromEntries(
    allDrafts.map((a) => [a.id, a]),
  )
  return resolveSlotArticles(slots, contentMap, articleById, poolArticles)
}

/**
 * Reader version — fetches only the article IDs referenced in the page blocks
 * via GET /articles?article_ids=... rather than pulling all drafts.
 */
export function usePublishedSlotArticles(
  slots: CanvasSlot[],
  contentMap: Record<string, BlockContent>,
  poolArticles?: WireArticle[],
): { slotArticleMap: Record<string, WireArticle[]>; isPending: boolean } {
  const staticIds = useMemo(() => {
    const ids = new Set<string>()
    for (const content of Object.values(contentMap)) {
      if (content?.source === 'static') {
        for (const id of content.articleIds ?? []) {
          if (id) ids.add(id)
        }
      }
    }
    return [...ids]
  }, [contentMap])

  const { data: articles = [], isLoading: isPending } = useArticlesByIds(staticIds)
  const articleById: Record<string, WireArticle> = Object.fromEntries(
    articles.map((a) => [a.id, a]),
  )

  const slotArticleMap = resolveSlotArticles(slots, contentMap, articleById, poolArticles)
  return { slotArticleMap, isPending }
}

// ─── Per-slot renderer ────────────────────────────────────────────────────────

const SPACER_PX: Record<string, number> = { sm: 8, md: 24, lg: 48, xl: 80 }

export function PageSlotRenderer({
  slot,
  articles,
  slotArticleMap,
  compact = false,
  imagePadding = false,
  onArticleClick,
  showPageMetrics = false,
  pageMetricsByArticleId = {},
  totalPageViewsFromPage = 0,
  metricsAnchor = 'right',
  activeBlockId = null,
  activeArticleId = null,
}: {
  slot: CanvasSlot
  articles: WireArticle[]
  slotArticleMap: Record<string, WireArticle[]>
  compact?: boolean
  imagePadding?: boolean
  onArticleClick?: (article: WireArticle) => void
  showPageMetrics?: boolean
  pageMetricsByArticleId?: Record<string, PageArticleInlineMetrics>
  totalPageViewsFromPage?: number
  metricsAnchor?: 'left' | 'right' | 'inline'
  activeBlockId?: string | null
  activeArticleId?: string | null
}) {
  const { user } = useWireAuth()
  const isLocked = (article: WireArticle) => Boolean(user && isArticleLocked(article, user))
  const [lockedArticle, setLockedArticle] = useState<WireArticle | null>(null)
  const handleArticleClick = onArticleClick
    ? (article: WireArticle) => {
        if (isLocked(article)) setLockedArticle(article)
        else onArticleClick(article)
      }
    : undefined
  const cfg = slot.config
  const slotIsActive = Boolean(activeBlockId && activeBlockId === slot.id)
  const slotContainsActiveBlock = Boolean(activeBlockId && (activeBlockId === slot.id || activeBlockId.startsWith(`${slot.id}:`)))
  const slotHasActiveArticle = Boolean(activeArticleId && articles.some(article => article.id === activeArticleId))
  const slotShouldDim = Boolean(activeBlockId) && !slotContainsActiveBlock && !slotHasActiveArticle
  const slotActiveArticleId = slotHasActiveArticle ? activeArticleId : null

  function renderSlot() {
    switch (slot.type) {
    case 'story-row': {
      if (articles.length === 0) return null
      const rawCount = Number(cfg.columnCount ?? 3)
      const columnCount = ([2, 3, 4, 5].includes(rawCount) ? rawCount : 3) as 2 | 3 | 4 | 5
      return (
        <StoryRow
          articles={articles.slice(0, columnCount)}
          config={{
            columnCount,
            imageAspectRatio: (cfg.imageAspectRatio as '16/9' | '4/3' | '1/1' | '3/4') ?? '4/3',
            sectionHeaderEnabled: Boolean(cfg.sectionHeaderEnabled),
            sectionHeaderTitle: (cfg.sectionHeaderTitle as string) ?? null,
            sectionHeaderStyle: (cfg.sectionHeaderStyle as 'rule-left' | 'all-caps' | 'plain') ?? 'rule-left',
            showImages: cfg.showImages !== false,
            showSummary: Boolean(cfg.showSummary),
          }}
          activeArticleId={slotActiveArticleId}
          onArticleClick={handleArticleClick ? (a) => handleArticleClick(a) : undefined}
        />
      )
    }

    case 'ranked-list':
      if (articles.length === 0) return null
      return (
        <RankedList
          articles={articles.slice(0, 10)}
          config={{
            sectionHeaderEnabled: Boolean(cfg.sectionHeaderEnabled),
            sectionHeaderTitle: (cfg.sectionHeaderTitle as string) ?? null,
            sectionHeaderStyle: (cfg.sectionHeaderStyle as 'rule-left' | 'all-caps' | 'plain') ?? 'rule-left',
            numberingStyle: (cfg.numberingStyle as 'numeric' | 'pill' | 'minimal') ?? 'numeric',
            showSummary: Boolean(cfg.showSummary),
            showFooterAction: Boolean(cfg.showFooterAction),
            footerActionLabel: (cfg.footerActionLabel as string) ?? 'Explore More',
          }}
          activeArticleId={slotActiveArticleId}
          onArticleClick={handleArticleClick ? (a) => handleArticleClick(a) : undefined}
        />
      )

    case 'unranked-list':
      if (articles.length === 0) return null
      return (
        <UnrankedList
          articles={articles.slice(0, 12)}
          config={{
            sectionHeaderEnabled: Boolean(cfg.sectionHeaderEnabled),
            sectionHeaderTitle: (cfg.sectionHeaderTitle as string) ?? null,
            sectionHeaderStyle: (cfg.sectionHeaderStyle as 'rule-left' | 'all-caps' | 'plain') ?? 'rule-left',
            showSummary: Boolean(cfg.showSummary),
          }}
          activeArticleId={slotActiveArticleId}
          onArticleClick={handleArticleClick ? (a) => handleArticleClick(a) : undefined}
        />
      )

    case 'inline-feature': {
      const article = articles[0]
      const showImage = cfg.showImage !== false
      const showSummary = cfg.showSummary !== false
      const imageSide = (cfg.imageSide as string) ?? 'right'
      const titleSize = compact ? 'text-sm' : 'text-3xl'
      const padding = compact ? 'p-4' : 'p-6 pr-8'
      const aspectRatio = compact ? 'aspect-[4/3]' : 'aspect-[2/1]'
      const gridCols = !showImage
        ? 'grid-cols-1'
        : compact
        ? 'grid-cols-[1fr_1fr]'
        : 'grid-cols-[2fr_3fr]'
      if (!article) return null
      const textCol = (
        <div className={`flex flex-col ${padding}`}>
          <h3 className={`font-zodiak ${titleSize} font-bold leading-tight text-fg`}>
            {isLocked(article) && <Lock size={14} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}
            ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {articleDisplayTitle(article)}
          </h3>
          {showSummary && (
            <p className={`mt-3 text-fg-secondary ${compact ? 'text-xs leading-4' : 'text-sm leading-6'}`}>{articleDescription(article)}</p>
          )}
          <div className="mt-auto pt-4">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">{article.regions?.[0]}</div>
            <div className="mt-1 text-[10px] uppercase tracking-wide text-fg-secondary">{formatArticleDate(article.publishedAt ?? article.updatedAt)}</div>
          </div>
        </div>
      )
      const imageCol = showImage ? (
        <div className={`${aspectRatio} ${imagePadding ? 'pr-3' : ''}`}>
          {articleThumbnailUrl(article) ? (
            <img src={articleThumbnailUrl(article)} alt={article.thumbnail?.altText ?? ''} className="h-full w-full object-cover" />
          ) : isLocked(article) ? (
            <div className="flex h-full items-center justify-center bg-fill">
              <Lock size={36} className="text-warning-fg" aria-hidden="true" />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center bg-fill text-fg-secondary">No image</div>
          )}
        </div>
      ) : null
      return (
        <div
          className={[
            `grid ${gridCols} overflow-hidden transition-all duration-200${handleArticleClick ? ' cursor-pointer' : ''}`,
            slotActiveArticleId === article.id ? 'bg-accent-primary-fill/35 ring-2 ring-inset ring-accent-primary-solid shadow-sm' : '',
            slotActiveArticleId && slotActiveArticleId !== article.id ? 'opacity-35 saturate-50' : '',
          ].join(' ')}
          onClick={handleArticleClick ? () => handleArticleClick(article) : undefined}
        >
          {imageSide === 'left' ? <>{imageCol}{textCol}</> : <>{textCol}{imageCol}</>}
        </div>
      )
    }

    case 'feature-with-rail': {
      const lead = articles[0]
      const supporting = articles.slice(1, 6)
      const showImage = cfg.showImage !== false
      const showSummary = cfg.showSummary !== false
      const supportingStyle = (cfg.supportingStyle as string) ?? 'compact'
      if (!lead) return null
      return (
        <div className="grid grid-cols-[2fr_1fr_1fr] divide-x divide-line">
          <div
            className={[
              `p-5 transition-all duration-200${handleArticleClick ? ' cursor-pointer' : ''}`,
              slotActiveArticleId === lead.id ? 'bg-accent-primary-fill/35 ring-2 ring-inset ring-accent-primary-solid shadow-sm' : '',
              slotActiveArticleId && slotActiveArticleId !== lead.id ? 'opacity-35 saturate-50' : '',
            ].join(' ')}
            onClick={handleArticleClick ? () => handleArticleClick(lead) : undefined}
          >
            {showImage && !articleThumbnailUrl(lead) && isLocked(lead) && (
              <div className="mb-3 flex aspect-video w-full items-center justify-center bg-fill">
                <Lock size={40} className="text-warning-fg" aria-hidden="true" />
              </div>
            )}
            {showImage && articleThumbnailUrl(lead) && (
              <img src={articleThumbnailUrl(lead)} alt={lead.thumbnail?.altText ?? ''} className="mb-3 aspect-video w-full object-cover" />
            )}
            {showImage && !isLocked(lead) && !articleThumbnailUrl(lead) && (
              <div className="mb-3 flex aspect-video w-full items-center justify-center bg-fill text-fg-secondary">No image</div>
            )}
            <h3 className="font-zodiak text-xl font-bold leading-tight text-fg">
              {isLocked(lead) && <Lock size={14} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}
              ({[lead.classification, ...(lead.disseminationControls ?? [])].join('//')}) {articleDisplayTitle(lead)}
            </h3>
            {showSummary && (
              <p className="mt-2 text-sm leading-6 text-fg-secondary">{articleDescription(lead)}</p>
            )}
            {lead.publishedAt && (
              <div className="mt-3 text-[10px] uppercase tracking-wide text-fg-secondary">{formatArticleDate(lead.publishedAt)}</div>
            )}
          </div>
          <div className="col-span-2 divide-y divide-line">
            {supporting.map((art, i) => (
              <div
                key={i}
                className={[
                  `flex gap-3 p-3 transition-all duration-200${handleArticleClick ? ' cursor-pointer' : ''}`,
                  slotActiveArticleId === art.id ? 'bg-accent-primary-fill/35 ring-2 ring-inset ring-accent-primary-solid shadow-sm' : '',
                  slotActiveArticleId && slotActiveArticleId !== art.id ? 'opacity-35 saturate-50' : '',
                ].join(' ')}
                onClick={handleArticleClick ? () => handleArticleClick(art) : undefined}
              >
                {!articleThumbnailUrl(art) && isLocked(art) && supportingStyle !== 'compact' && (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center bg-fill">
                    <Lock size={24} className="text-warning-fg" aria-hidden="true" />
                  </div>
                )}
                {articleThumbnailUrl(art) && supportingStyle !== 'compact' && (
                  <img src={articleThumbnailUrl(art)} alt={art.thumbnail?.altText ?? ''} className="h-16 w-16 shrink-0 object-cover" />
                )}
                <div>
                  <div className="font-zodiak text-xs font-bold leading-snug text-fg">
                    {isLocked(art) && <Lock size={11} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}
                    ({[art.classification, ...(art.disseminationControls ?? [])].join('//')}) {articleDisplayTitle(art)}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-wide text-fg-secondary">{art.regions?.[0]}</div>
                  {supportingStyle === 'teaser' && art.description && (
                    <p className="mt-1 text-[10px] leading-4 text-fg-secondary">{art.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    case 'hero-overlay': {
      const lead = articles[0]
      const secondary = articles.slice(1, 3)
      const showRightOverlay = cfg.showRightOverlay !== false
      const showSummary = cfg.showSummary !== false
      const intensity = (cfg.overlayIntensity as string) ?? 'medium'
      const overlayOpacity = intensity === 'low' ? 'bg-black/30' : intensity === 'high' ? 'bg-black/80' : 'bg-black/60'
      if (!lead) return null
      return (
        <div className="relative overflow-hidden rounded border border-line" style={{ minHeight: 300 }}>
          {articleThumbnailUrl(lead) ? (
            <img src={articleThumbnailUrl(lead)} alt={lead.thumbnail?.altText ?? ''} className="absolute inset-0 h-full w-full object-cover" />
          ) : isLocked(lead) ? (
            <div className="absolute inset-0 flex items-center justify-center bg-surface">
              <Lock size={44} className="text-amber-400" aria-hidden="true" />
            </div>
          ) : (
            <div className="absolute inset-0 bg-gray-400" />
          )}
          <div className={`absolute inset-0 ${overlayOpacity}`} />
          <div
            className={`relative grid h-full ${showRightOverlay ? 'grid-cols-[3fr_1fr]' : 'grid-cols-1'}`}
            style={{ minHeight: 300 }}
          >
            <div
              className={[
                `flex flex-col justify-end p-6 transition-all duration-200${handleArticleClick ? ' cursor-pointer' : ''}`,
                slotActiveArticleId === lead.id ? 'bg-accent-primary-fill/25 ring-2 ring-inset ring-accent-primary-solid shadow-sm' : '',
                slotActiveArticleId && slotActiveArticleId !== lead.id ? 'opacity-35 saturate-50' : '',
              ].join(' ')}
              onClick={handleArticleClick ? () => handleArticleClick(lead) : undefined}
            >
              <div className="mb-2">
                <span className="bg-red-9 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                  Top News
                </span>
              </div>
              <h3 className="font-zodiak text-3xl font-bold leading-tight text-white">
                {isLocked(lead) && <Lock size={16} className="mr-0.5 inline-block align-middle text-amber-400" aria-label="Access restricted" />}
                ({[lead.classification, ...(lead.disseminationControls ?? [])].join('//')}) {articleDisplayTitle(lead)}
              </h3>
              {showSummary && (
                <p className="mt-2 text-sm text-white/70">{articleDescription(lead)}</p>
              )}
              <div className="mt-3 text-[9px] uppercase tracking-wide text-white/50">
                {lead.regions?.[0]} · {formatArticleDate(lead.publishedAt ?? lead.updatedAt)}
              </div>
            </div>
            {showRightOverlay && (
              <div className="flex flex-col justify-center gap-4 border-l border-white/20 bg-black/40 p-4">
                {secondary.map((art, i) => (
                  <div
                    key={i}
                    className={[
                      `border-b border-white/20 pb-3 transition-all duration-200 last:border-0 last:pb-0${handleArticleClick ? ' cursor-pointer' : ''}`,
                      slotActiveArticleId === art.id ? 'bg-accent-primary-fill/25 ring-1 ring-inset ring-accent-primary-solid rounded-sm px-2 py-1 shadow-sm' : '',
                      slotActiveArticleId && slotActiveArticleId !== art.id ? 'opacity-35 saturate-50' : '',
                    ].join(' ')}
                    onClick={handleArticleClick ? () => handleArticleClick(art) : undefined}
                  >
                    <div className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-white/50">
                      {art.regions?.[0]}
                    </div>
                    <div className="font-zodiak text-xs font-bold leading-snug text-white">
                      {isLocked(art) && <Lock size={11} className="mr-0.5 inline-block align-middle text-amber-400" aria-label="Access restricted" />}
                      ({[art.classification, ...(art.disseminationControls ?? [])].join('//')}) {articleDisplayTitle(art)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )
    }

    case 'livewire-callout': {
      const article = articles[0]
      const visualStyle = (cfg.visualStyle as string) ?? 'tile'
      const showDuration = cfg.showDuration !== false
      if (!article) return null

      if (visualStyle === 'banner') {
        return (
          <div
            className={[
              `flex items-stretch overflow-hidden rounded border border-line transition-all duration-200${handleArticleClick ? ' cursor-pointer' : ''}`,
              slotActiveArticleId === article.id ? 'bg-accent-primary-fill/35 ring-2 ring-accent-primary-solid shadow-sm' : '',
              slotActiveArticleId && slotActiveArticleId !== article.id ? 'opacity-35 saturate-50' : '',
            ].join(' ')}
            onClick={handleArticleClick ? () => handleArticleClick(article) : undefined}
          >
            {article.thumbnail?.presignedUrl ? (
              <img src={article.thumbnail.presignedUrl} alt={article.thumbnail?.altText ?? ''} className="h-24 w-36 shrink-0 object-cover" />
            ) : isLocked(article) ? (
              <div className="flex h-24 w-36 shrink-0 items-center justify-center bg-fill">
                <Lock size={32} className="text-warning-fg" aria-hidden="true" />
              </div>
            ) : null}
            <div className="flex flex-1 items-center gap-4 bg-fill px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-solid text-sm text-white">▶</div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Live Intelligence Briefing</div>
                <div className="font-zodiak text-lg font-bold text-fg">{isLocked(article) && <Lock size={13} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.title}</div>
                {showDuration && <div className="mt-1 text-[10px] text-fg-secondary">{formatArticleDate(article.publishedAt ?? article.updatedAt)}</div>}
              </div>
            </div>
          </div>
        )
      }

      if (visualStyle === 'compact') {
        return (
          <div
          className={[
            `flex items-center gap-3 border-b border-line px-3 py-2.5 transition-all duration-200${handleArticleClick ? ' cursor-pointer' : ''}`,
            slotActiveArticleId === article.id ? 'bg-accent-primary-fill/35 ring-2 ring-inset ring-accent-primary-solid shadow-sm' : '',
            slotActiveArticleId && slotActiveArticleId !== article.id ? 'opacity-35 saturate-50' : '',
          ].join(' ')}
            onClick={handleArticleClick ? () => handleArticleClick(article) : undefined}
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-solid text-xs text-white">▶</div>
            <div className="flex-1 font-zodiak text-sm font-bold text-fg">{isLocked(article) && <Lock size={11} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.title}</div>
            {showDuration && <div className="shrink-0 text-[10px] text-fg-secondary">{formatArticleDate(article.publishedAt ?? article.updatedAt)}</div>}
          </div>
        )
      }

      // tile (default)
      return (
        <div
          className={[
            `flex items-center gap-4 rounded border border-line bg-fill px-4 py-3 transition-all duration-200${handleArticleClick ? ' cursor-pointer' : ''}`,
            slotActiveArticleId === article.id ? 'bg-accent-primary-fill/45 ring-2 ring-accent-primary-solid shadow-sm' : '',
            slotActiveArticleId && slotActiveArticleId !== article.id ? 'opacity-35 saturate-50' : '',
          ].join(' ')}
          onClick={handleArticleClick ? () => handleArticleClick(article) : undefined}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-solid text-sm text-white">▶</div>
          <div className="flex-1">
            <div className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Live Now</div>
            <div className="font-zodiak text-base font-bold text-fg">{isLocked(article) && <Lock size={13} className="mr-0.5 inline-block align-middle text-warning-fg" aria-label="Access restricted" />}({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.title}</div>
            {showDuration && <div className="mt-1 text-[10px] text-fg-secondary">{formatArticleDate(article.publishedAt ?? article.updatedAt)}</div>}
          </div>
        </div>
      )
    }

    case 'editorial-grid': {
      const preset = (cfg.layoutPreset as EditorialGridLayoutPreset) ?? '3-equal'
      const presetCfg = EDITORIAL_GRID_PRESET_CONFIG[preset]
      const colKeys = COL_KEYS.slice(0, presetCfg.columns)
      const allColsEmpty = colKeys.every(colKey =>
        (slot.columnBlocks?.[colKey] ?? []).every(
          (cb) => (slotArticleMap[`${slot.id}:${colKey}:${cb.id}`] ?? []).length === 0
        )
      )
      if (allColsEmpty) return null
      const showDividers = cfg.showDividers !== false

      // Only render columns that have at least one article; recompute grid template to match
      const visibleColKeys = colKeys.filter(colKey =>
        (slot.columnBlocks?.[colKey] ?? []).some(
          (cb) => (slotArticleMap[`${slot.id}:${colKey}:${cb.id}`] ?? []).length > 0
        )
      )
      const gridTemplate = `repeat(${visibleColKeys.length}, 1fr)`

      return (
        <div
          className={`overflow-visible ${showDividers ? 'divide-x divide-line' : ''}`}
          style={{ display: 'grid', gridTemplateColumns: gridTemplate }}
        >
          {visibleColKeys.map((colKey, i) => {
            const colBlocks = slot.columnBlocks?.[colKey] ?? []
            const isLastCol = i === visibleColKeys.length - 1
            return (
              <div key={colKey} className="min-w-0 overflow-visible">
                {colBlocks.map((colBlock, j) => {
                  const colContentKey = `${slot.id}:${colKey}:${colBlock.id}`
                  const colPrefix = `${colKey}:`
                  const colConfig: Record<string, string | number | boolean | null> = {
                    ...PAGE_BLOCK_REGISTRY[colBlock.type as CanvasSlot['type']].defaultConfig,
                    ...Object.fromEntries(
                      Object.entries(cfg)
                        .filter(([k]) => k.startsWith(colPrefix))
                        .map(([k, v]) => [k.slice(colPrefix.length), v as string | number | boolean | null]),
                    ),
                  }
                  const colSlot: CanvasSlot = {
                    id: colContentKey,
                    type: colBlock.type as Parameters<typeof PageSlotRenderer>[0]['slot']['type'],
                    order: j,
                    config: colConfig,
                  }
                  const colArticles = slotArticleMap[colContentKey] ?? []
                  return (
                    <PageSlotRenderer
                      key={colBlock.id}
                      slot={colSlot}
                      articles={colArticles}
                      slotArticleMap={slotArticleMap}
                      compact
                      imagePadding={showDividers && !isLastCol}
                      onArticleClick={handleArticleClick}
                      showPageMetrics={showPageMetrics}
                      pageMetricsByArticleId={pageMetricsByArticleId}
                      totalPageViewsFromPage={totalPageViewsFromPage}
                      metricsAnchor={i === 0 ? 'left' : isLastCol ? 'right' : 'inline'}
                      activeBlockId={activeBlockId}
                      activeArticleId={activeArticleId}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>
      )
    }

    case 'section-divider': {
      const style = (cfg.style as string) ?? 'plain'
      const label = cfg.label as string | null
      return (
        <div className="flex items-center gap-3 py-2">
          {style === 'labeled' && label ? (
            <>
              <div className="flex-1 border-t border-line" />
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">{label}</span>
              <div className="flex-1 border-t border-line" />
            </>
          ) : (
            <div
              className={`w-full border-t ${style === 'thick' ? 'border-t-2 border-fg-secondary opacity-40' : 'border-line'}`}
            />
          )}
        </div>
      )
    }

    case 'spacer': {
      const size = (cfg.size as string) ?? 'md'
      return <div style={{ height: SPACER_PX[size] ?? 24 }} />
    }

    default:
      return null
    }
  }

  // story-row, ranked-list, unranked-list render section headers internally via their
  // component configs. All other content blocks need the header injected here.
  const HANDLES_HEADER_INTERNALLY = new Set(['story-row', 'ranked-list', 'unranked-list'])
  const slotContent = renderSlot()
  const slotMetricRows = showPageMetrics
    ? articles
        .map(article => ({
          articleId: article.id,
          title: articleDisplayTitle(article),
          viewsFromPage: pageMetricsByArticleId[article.id]?.viewsFromPage ?? 0,
        }))
        .filter(row => row.viewsFromPage > 0)
    : []
  const showExternalHeader =
    slotContent !== null &&
    !HANDLES_HEADER_INTERNALLY.has(slot.type) &&
    Boolean(cfg.sectionHeaderEnabled) &&
    Boolean(cfg.sectionHeaderTitle)
  const showSlotMetrics = slotContent !== null && slot.type !== 'editorial-grid' && slotMetricRows.length > 0
  const extHeaderTitle = cfg.sectionHeaderTitle as string
  const extHeaderStyle = (cfg.sectionHeaderStyle as string) ?? 'rule-left'

  function renderExternalHeader() {
    if (extHeaderStyle === 'all-caps') {
      return (
        <header className="mb-3">
          <h3 className="text-xs font-bold uppercase tracking-widest text-fg-secondary">{extHeaderTitle}</h3>
        </header>
      )
    }
    if (extHeaderStyle === 'plain') {
      return (
        <header className="mb-3">
          <h3 className="text-sm font-semibold text-fg">{extHeaderTitle}</h3>
        </header>
      )
    }
    return (
      <header className="mb-3 border-b border-line pb-1">
        <h3 className="text-xs font-bold uppercase tracking-wider text-fg-secondary">{extHeaderTitle}</h3>
      </header>
    )
  }

  return (
    <>
      {showExternalHeader && renderExternalHeader()}
      {slotContent !== null ? (
        <div className={[
          showSlotMetrics && metricsAnchor !== 'inline' ? 'relative' : '',
          'transition-all duration-200',
          slotIsActive ? 'ring-2 ring-accent-primary-solid rounded-lg bg-accent-primary-fill/25 shadow-md' : '',
          slotShouldDim ? 'opacity-35 saturate-50' : '',
        ].join(' ')}>
          {slotContent}
          {showSlotMetrics && (
            <PageSlotInlineMetricsCard
              rows={slotMetricRows}
              anchor={metricsAnchor}
              totalPageViews={totalPageViewsFromPage}
              blockId={slot.id}
              activeBlockId={activeBlockId}
              activeArticleId={slotActiveArticleId}
            />
          )}
        </div>
      ) : null}
      <ArticleAccessDialog article={lockedArticle} onClose={() => setLockedArticle(null)} />
    </>
  )
}
